import { scheduleRoadmap } from './utils/roadmapSchedule';
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ProductManagerRoadmapDisplay from './ProductManagerRoadmapDisplay';
import ProgressTracker from './ProgressTracker';
import { getFeatureFlag } from './featureFlags';
import { ChevronDown } from 'lucide-react';
import RoadmapGate from './components/RoadmapGate';

const validationSchema = Yup.object().shape({
  hoursPerWeek: Yup.number().required('Required').positive('Must be positive').integer('Must be an integer'),
  experienceLevel: Yup.string().required('Required'),
  technicalBackground: Yup.string().required('Required'),
  budget: Yup.string().required('Required'),
  learningStyle: Yup.string().required('Required'),
  salaryGoal6Months: Yup.number().required('Required').positive('Must be positive').integer('Must be an integer'),
  salaryGoal18Months: Yup.number().required('Required').positive('Must be positive').integer('Must be an integer'),
  workingFullTime: Yup.string().required('Required'),
});

function ProductManagerQuestionnaire({ formData, handleInputChange, generateRoadmap, roadmap, isSubscribed, setIsSubscribed, progress, updateProgress }) {
  const [showScrollArrow, setShowScrollArrow] = useState(false);

  const initialValues = {
    hoursPerWeek: '',
    experienceLevel: '',
    technicalBackground: '',
    budget: '',
    learningStyle: '',
    salaryGoal6Months: '',
    salaryGoal18Months: '',
    workingFullTime: '',
  };

  const certificationDetails = {
    "Product Management Fundamentals": { baseDuration: 4, cost: 0 },
    "Agile Certified Practitioner (PMI-ACP)": { baseDuration: 6, cost: 495 },
    "Certified Scrum Product Owner (CSPO)": { baseDuration: 2, cost: 995 },
    "PRINCE2 Agile Foundation": { baseDuration: 3, cost: 200 },
    "ISTQB Certified Tester Foundation Level v4.0": { baseDuration: 3, cost: 192 },
    "BCS Practitioner Certificate in Digital Product Management": { baseDuration: 3, cost: 222 },
    "Disciplined Agile Senior Scrum Master (DASM) Certification": { baseDuration: 3, cost: 150 },

  };

  const generateProductManagerRoadmap = (values) => {
    let roadmapItems = [];
    let certifications = [];
    let timelineMessage = '';
    let messageColor = '';
    const {
      hoursPerWeek, budget, experienceLevel, learningStyle, technicalBackground,
      salaryGoal6Months, salaryGoal18Months, workingFullTime
    } = values;

    // Adjust timeline based on hours per week
    let timeMultiplier = 14 / hoursPerWeek;
    let totalMonths = 6;

    const addRoadmapItem = (title, durationWeeks, description, startMonth, endMonth, coveredInBootcamp = true) => {
      const adjustedDuration = Math.ceil(durationWeeks * timeMultiplier);
      roadmapItems.push({
        title: title.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        duration: adjustedDuration,
        startMonth,
        endMonth,
        description: description.charAt(0).toUpperCase() + description.slice(1),
        type: 'skill',
        coveredInBootcamp: coveredInBootcamp
      });
    };
    
    const addCertification = (name, durationWeeks, cost, startMonth, endMonth, coveredInBootcamp = false) => {
      const adjustedDuration = Math.ceil(durationWeeks * timeMultiplier);
      certifications.push({
        name: name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        duration: adjustedDuration,
        startMonth,
        endMonth,
        cost,
        type: 'certification',
        coveredInBootcamp: coveredInBootcamp
      });
    };

    // Add Product Manager-specific roadmap items and certifications
    addRoadmapItem("UI/UX Fundamentals", 5, "Learn Figma, PDLC, SDLC, Agile, MVP, JIRA", 1, 1, true);
    addRoadmapItem("Software Engineering Fundamentals", 2, "Learn basic software engineering principles", 1, 1, true);
    addRoadmapItem("Prince 2", 5, "Project management methodology", 2, 2, true);
    addRoadmapItem("Product Management Fundamentals", 4, "Product Strategy, OKRs, KPIs, Risk Management and RAID logs", 3, 3, true);
    addCertification("Agile Certified Practitioner (PMI-ACP)", 6, 495, 2, 3, true);
    addCertification("Certified Scrum Product Owner (CSPO)", 2, 995, 4, 4, true);
    addCertification("PRINCE2 Agile Foundation", 2, 200, 4, 5, true);
    addCertification("Professional Diploma in UX Design", 2, 2200, 4, 6);
    addCertification("BCS Practitioner Certificate in Digital Product Management", 2, 222, 4, 6);
    addCertification("Disciplined Agile Senior Scrum Master (DASM) Certification", 2, 150, 4, 6);
    addCertification("ISTQB Certified Tester Foundation Level v4.0", 2, 192, 3, 4);
    addRoadmapItem("Interview Prep and CV", 1, "Prepare for job interviews and update CV", 6, 6);

    // Budget-based certification selection
    let totalCertificationCost = 0;
    let budgetLimit = parseInt(budget);

    if (budget === "2500") {
      budgetLimit = Infinity; // No budget limit for "£2,000+"
    }

    certifications = certifications.filter(cert => {
      if (totalCertificationCost + cert.cost <= budgetLimit) {
        totalCertificationCost += cert.cost;
        return true;
      }
      return false;
    });

    // Add message about certifications based on budget
    if (budgetLimit === Infinity) {
      timelineMessage += "All certifications included based on your budget.";
    } else if (totalCertificationCost < budgetLimit) {
      timelineMessage += `Certifications within your budget of £${budgetLimit} included.`;
    } else {
      timelineMessage += `Some certifications omitted due to budget limit of £${budgetLimit}.`;
    }

    // Adjust for experience level
    if (experienceLevel === 'Beginner') {
      addRoadmapItem("Introduction to Product Management", 2, "Overview of product management principles", 1, 1);
    } else if (experienceLevel === 'Advanced') {
      timelineMessage += " Advanced track: Some basic items removed.";
      messageColor = "text-green-400";
    }

    // Adjust for technical background
    if (technicalBackground === 'None' || technicalBackground === 'Basic') {
      addRoadmapItem("Technical Fundamentals for PMs", 3, "Learn basic technical concepts relevant to product management", 2, 2);
    }

    // Adjust for full-time work
    if (workingFullTime === 'yes') {
      timelineMessage += " As you're working full-time, we recommend utilizing evening training and learning on the move.";
    } else {
      timelineMessage += " As you're not in full-time work, we recommend completing 20-40hrs per week on up-skilling.";
    }

    // Adjust roadmap based on salary goals
    const salary6Months = parseInt(salaryGoal6Months);
    const salary18Months = parseInt(salaryGoal18Months);

    if (salary6Months > 70000 || salary18Months > 90000) {
      addRoadmapItem("Advanced Product Management Skills", 4, "Learn advanced techniques and strategies in product management", 5, 6);
      timelineMessage += " Your ambitious salary goals may require additional advanced skills and experience.";
      messageColor = "text-yellow-500";
    }

    generateRoadmap(scheduleRoadmap({
      items: [...roadmapItems, ...certifications], timelineMessage, messageColor, totalMonths
    }));
    setShowScrollArrow(true);
  };

  useEffect(() => {
    if (showScrollArrow) {
      const timer = setTimeout(() => {
        setShowScrollArrow(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showScrollArrow]);

  const scrollToRoadmap = () => {
    const roadmapElement = document.getElementById('roadmap-section');
    if (roadmapElement) {
      roadmapElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-900 text-white py-6 relative">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 text-green-400">Tekyu</h1>
          <h2 className="text-3xl font-bold mb-2 text-white">Product Manager Roadmap Calculator</h2>
          <p className="text-xl text-slate-300">Elevate Your Career in Product Management</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              generateProductManagerRoadmap(values);
              setSubmitting(false);
            }}
          >
            {({ values, isSubmitting }) => (
              <Form className="rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 mb-8 border border-slate-700 shadow-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <label htmlFor="hoursPerWeek" className="text-sm font-semibold mb-1 text-green-400">Hours You Can Commit Per Week</label>
                    <Field
                      type="number"
                      name="hoursPerWeek"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                      placeholder="e.g., 14"
                    />
                    <ErrorMessage name="hoursPerWeek" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <div className="flex flex-col">
                    <label htmlFor="experienceLevel" className="text-sm font-semibold mb-1 text-green-400">Your Experience Level</label>
                    <Field
                      as="select"
                      name="experienceLevel"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                    >
                      <option value="">Select Experience Level</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </Field>
                    <ErrorMessage name="experienceLevel" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <div className="flex flex-col">
                    <label htmlFor="technicalBackground" className="text-sm font-semibold mb-1 text-green-400">Technical Background</label>
                    <Field
                      as="select"
                      name="technicalBackground"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                    >
                      <option value="">Select Technical Background</option>
                      <option value="None">None</option>
                      <option value="Basic">Basic</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </Field>
                    <ErrorMessage name="technicalBackground" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <div className="flex flex-col">
                    <label htmlFor="budget" className="text-sm font-semibold mb-1 text-green-400">Budget For Learning/Certifications (£)</label>
                    <Field
                      as="select"
                      name="budget"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                    >
                      <option value="">Select Budget</option>
                      <option value="0">£0 — free learning resources</option>
                      <option value="500">£500</option>
                      <option value="1000">£1,000</option>
                      <option value="1500">£1,500</option>
                      <option value="2000">£2,000</option>
                      <option value="2500">£2,000+</option>
                    </Field>
                    <ErrorMessage name="budget" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <div className="flex flex-col">
                    <label htmlFor="learningStyle" className="text-sm font-semibold mb-1 text-green-400">Preferred Learning Style</label>
                    <Field
                      as="select"
                      name="learningStyle"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                    >
                      <option value="">Select Learning Style</option>
                      <option value="self-paced">Self-Paced</option>
                      <option value="structured">Structured</option>
                    </Field>
                    <ErrorMessage name="learningStyle" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <div className="flex flex-col">
                    <label htmlFor="salaryGoal6Months" className="text-sm font-semibold mb-1 text-green-400">Salary Goal In 6 Months (£)</label>
                    <Field
                      type="number"
                      name="salaryGoal6Months"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                      placeholder="e.g., 50000"
                    />
                    <ErrorMessage name="salaryGoal6Months" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="flex flex-col">
                    <label htmlFor="salaryGoal18Months" className="text-sm font-semibold mb-1 text-green-400">Salary Goal In 18 Months (£)</label>
                    <Field
                      type="number"
                      name="salaryGoal18Months"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                      placeholder="e.g., 70000"
                    />
                    <ErrorMessage name="salaryGoal18Months" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="flex flex-col">
                    <label htmlFor="workingFullTime" className="text-sm font-semibold mb-1 text-green-400">Are You Currently Working Full-Time? (40hrs)</label>
                    <Field
                      as="select"
                      name="workingFullTime"
                      className="px-4 py-3 bg-slate-900 border border-slate-600 focus:border-green-400 focus:ring-1 focus:ring-green-400 w-full rounded-lg focus:outline-none text-white transition-colors"
                    >
                      <option value="">Select Option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Field>
                    <ErrorMessage name="workingFullTime" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-6 bg-green-400 w-full text-white px-4 py-3 rounded-md focus:outline-none hover:bg-green-400 transition-colors duration-300 ease-in-out font-bold"
                >
                  Generate Roadmap
                </button>
              </Form>
            )}
          </Formik>
          
          {roadmap && (
            <div id="roadmap-section">
              <RoadmapGate roadmap={roadmap} roleKey="product-manager">
                {({ isUnlocked, maxMonth }) => (
                  <>
                    {roadmap.timelineMessage && (
                      <p className={`text-center mb-4 font-bold ${roadmap.messageColor}`}>
                        {roadmap.timelineMessage}
                      </p>
                    )}
                    <ProductManagerRoadmapDisplay roadmap={roadmap} isUnlocked={isUnlocked} maxMonth={maxMonth} />
                  </>
                )}
              </RoadmapGate>
            </div>
          )}
          
          {getFeatureFlag('showProgressTracker') && (
            <div className="bg-slate-800 shadow-lg rounded-lg p-8 border border-green-400 mt-8">
              <ProgressTracker 
                roadmap={roadmap} 
                isSubscribed={isSubscribed} 
                setIsSubscribed={setIsSubscribed}
                progress={progress}
                updateProgress={updateProgress}
              />
            </div>
          )}
        </div>
      </div>

      {showScrollArrow && (
        <button 
          onClick={scrollToRoadmap}
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-green-400 text-white p-4 rounded-full shadow-lg animate-bounce z-50"
          style={{ width: '60px', height: '60px' }}
          aria-label="Scroll to roadmap"
        >
          <ChevronDown size={36} />
        </button>
      )}
    </main>
  );
}

export default ProductManagerQuestionnaire;